/** Figma node 1001:431 — platform overview rows + “Trusted by” tabs */

export const homePlatformOverviewFigma = {
  eyebrow: 'PLATFORM OVERVIEW',
  lead: 'Complete Device-to-Cloud Solutions for Your Brand',
  features: [
    { id: 'mts', title: 'Multi Tenant System' },
    { id: 'mac', title: 'Mac Tracking and Troubleshooting' },
    { id: 'device', title: 'Device Management' },
    { id: 'customer', title: 'Customer Management' },
  ],
  phoneImage: '/images/home/platform-phone.png',
  phoneImageAlt: 'Mobile app preview with rooms and energy usage',
  dashboardImage: '/images/home/platform-dashboard-ui.png',
  dashboardImageAlt: 'Partner dashboard with tenants and workspace',
};

export const homeHero = {
  titleLine1: 'Build and Launch Your Own',
  titleLine2Highlight: 'Smart Home',
  titleLine2Rest: ' Devices with a Complete',
  titleLine3: 'IoT Platform',
  subtitle:
    'Firmware, cloud infrastructure, mobile apps, and device management — everything you need to create and scale smart home products and solutions.',
  trustLine: 'Trusted by 10,000+ teams worldwide',
  primaryCta: { label: 'Book Demo', to: '/contact-us' },
  heroImage: '/images/home/hero-dashboard.png',
  heroImageAlt: 'Smart home platform dashboard preview',
  miniCards: [
    { id: 'plug', label: 'Smart Plug', icon: 'plug' as const },
    { id: 'bulb', label: 'Smart Bulb', icon: 'bulb' as const },
  ],
  switchCard: { label: 'Smart Switch', status: 'Online', state: 'ON' as const },
} as const;

export const homeLogoStrip = {
  title: 'Chosen and trusted by around 50+ global Brands',
  logos: [
    { src: '/images/home/partners/tecnolite.png', alt: 'Tecnolite' },
    { src: '/images/home/partners/telkom.png', alt: 'Telkom Indonesia' },
    { src: '/images/home/partners/scg.png', alt: 'SCG' },
    { src: '/images/home/partners/globe.png', alt: 'Globe' },
    { src: '/images/home/partners/calex.png', alt: 'Calex Holland' },
    { src: '/images/home/partners/schneider.png', alt: 'Schneider Electric' },
    { src: '/images/home/partners/philips.png', alt: 'Philips' },
    { src: '/images/home/partners/haier.png', alt: 'Haier' },
  ],
};

/** Figma Io-Website node 1055:1792 — How The Platform Works */
export const homePlatformWorks = {
  title: 'How The Platform Works',
  subtitle: 'Four simple steps to launch your smart device business',
  phones: [
    {
      id: 'device-control',
      src: '/images/home/platform-works/phone-1.png',
      alt: 'Mobile app screen: device control for a smart lamp',
      width: 305,
      height: 588,
    },
    {
      id: 'my-devices',
      src: '/images/home/platform-works/phone-2.png',
      alt: 'Mobile app screen: my devices organized by room',
      width: 316,
      height: 601,
    },
    {
      id: 'home-dashboard',
      src: '/images/home/platform-works/phone-3.png',
      alt: 'Mobile app screen: home dashboard with energy and favorites',
      width: 310,
      height: 601,
    },
  ],
} as const;

export const homeFeatureCards = [
  {
    id: 'cloud',
    title: 'IoT Cloud Platform',
    description:
      'Scalable cloud infrastructure for device connectivity, data processing, and real-time communication',
    icon: 'cloud' as const,
    cardBg: '#e7ecff',
    mediaImage: '/images/home/feature-cloud.jpg',
  },
  {
    id: 'firmware',
    title: 'Device Firmware',
    description:
      'Production-ready firmware for ESP32, ESP8266, and other IoT chipsets with built-in connectivity',
    icon: 'cpu' as const,
    cardBg: '#93bfff',
    mediaImage: '/images/home/feature-firmware.jpg',
  },
  {
    id: 'analytics',
    title: 'Device Analytics',
    description:
      'Real-time monitoring, usage patterns, and performance metrics for your entire device fleet',
    icon: 'chart' as const,
    cardBg: '#f7f3ea',
    mediaImage: '/images/home/feature-analytics.jpg',
  },
  {
    id: 'dashboard',
    title: 'Partner Dashboard',
    description:
      'Comprehensive admin panel for device management, user analytics, and business insights',
    icon: 'layout' as const,
    cardBg: '#d1fafe',
    mediaImage: '/images/home/partner-dashboard.png',
  },
  {
    id: 'mobile',
    title: 'White-Label Mobile Apps',
    description: 'Fully customizable iOS and Android apps with your branding, ready to publish',
    icon: 'smartphone' as const,
    cardBg: '#ffdbdb',
    mediaImage: '/images/home/white-label-app-image.png',
  },
  {
    id: 'ota',
    title: 'OTA Updates',
    description:
      'Seamless over-the-air firmware updates with rollback capabilities and version control',
    icon: 'refresh' as const,
    cardBg: '#ffddfb',
    mediaImage: '/images/home/ota_updates_android.png',
  },
];

/** @deprecated — replaced by `homePlatformOverviewFigma` (Figma Frame 156) */
export const homePlatformSection = {
  eyebrow: 'PLATFORM OVERVIEW',
  lead:
    'End-to-end software, cloud, and mobile stack so you can ship certified devices faster—with your brand on every surface.',
};

/** @deprecated — replaced by static cards in `homePlatformOverviewFigma` */
export const homePlatformAccordionSets: {
  id: string;
  items: { id: string; title: string; body: string }[];
}[] = [
  {
    id: 'set-a',
    items: [
      {
        id: 'a1',
        title: 'Cloud & connectivity',
        body:
          'AWS IoT Core, MQTT with TLS, device provisioning, and multi-region deployment patterns for resilient fleets.',
      },
      {
        id: 'a2',
        title: 'Mobile experience',
        body:
          'White-label apps for device control, scenes, automations, and user management—ready for store submission.',
      },
      {
        id: 'a3',
        title: 'Firmware & hardware',
        body:
          'ESP32/8266 SDK, OTA, and reference integrations so your hardware team focuses on differentiation—not plumbing.',
      },
      {
        id: 'a4',
        title: 'Security & compliance',
        body:
          'Certificate-based auth, encrypted transport, and operational practices aligned with enterprise expectations.',
      },
    ],
  },
  {
    id: 'set-b',
    items: [
      {
        id: 'b1',
        title: 'Implementation timeline',
        body:
          'A structured 3-month path from hardware bring-up to branded apps and production cloud—documented in our product guide.',
      },
      {
        id: 'b2',
        title: 'APIs & integrations',
        body:
          'MQTT topics, REST, and extension points to connect ERP, support, and analytics systems you already use.',
      },
      {
        id: 'b3',
        title: 'Operations & support',
        body:
          'Dashboards for fleet health, release management, and partner workflows as you scale beyond the first SKU.',
      },
      {
        id: 'b4',
        title: 'Go-to-market',
        body:
          'Positioning blocks, documentation, and technical collateral your sales team can reuse with OEM customers.',
      },
    ],
  },
];

export const homeManufacturers = {
  title: 'Trusted by Leading Manufacturers',
  subtitle: 'Companies across industries rely on our platform to power smart device ecosystems',
  tabs: [
    {
      id: 'appliances',
      label: 'Home Appliances',
      body:
        'From certification-ready firmware to fleet dashboards, OEM teams launch connected appliance lines in months—not years.',
    },
    {
      id: 'lighting',
      label: 'Lighting Solutions',
      body:
        'Scene control, provisioning, and OTA tuned for lighting SKUs—one platform for residential and commercial programs.',
    },
    {
      id: 'building',
      label: 'Building Automation',
      body:
        'Integrations and tenant-aware policies for BMS and smart-building rollouts across sites and portfolios.',
    },
    {
      id: 'energy',
      label: 'Energy Management',
      body:
        'Telemetry and reporting for energy-focused devices—monitor load, schedule demand, and prove savings to customers.',
    },
  ],
};

export const homeDeviceCategories = {
  titleBefore: 'Build Any ',
  titleAccent: 'Smart Device',
  subtitle: 'Build and launch any type of smart home device with our comprehensive platform',
  /** Inner photo well — Figma `#93bfff` */
  photoWellBg: '#93bfff',
  /** Single row L→R (aligned grid) — outer card backgrounds from Figma */
  cards: [
    {
      id: 'switches',
      title: 'Smart Switches',
      cardBg: '#e7ecff',
      image: '/images/home/devices/smart-switches.jpg',
      alt: 'Finger pressing a circular smart switch on a wooden surface',
    },
    {
      id: 'lighting',
      title: 'Smart Lighting',
      cardBg: '#ffce80',
      image: '/images/home/devices/smart-lighting.jpg',
      alt: 'Living room with modern floor lamp',
    },
    {
      id: 'plugs',
      title: 'Smart Plugs',
      cardBg: '#ff89d8',
      image: '/images/home/devices/smart-plugs.jpg',
      alt: 'Wall-mounted smart plug with blue glow',
    },
    {
      id: 'fans',
      title: 'Smart Fans',
      cardBg: '#93bfff',
      image: '/images/home/devices/smart-fans.jpg',
      alt: 'Ceiling fan with wall remote',
    },
    {
      id: 'sensors',
      title: 'Sensors',
      cardBg: '#f7f3ea',
      image: '/images/home/devices/sensors.jpg',
      alt: 'Hand using a wall-mounted smart control panel',
    },
  ],
};

export const homeTestimonial = {
  quote:
    'We needed a credible IoT story for retailers in under a quarter. Aeviot gave us cloud, firmware, and apps in one stack—our team stayed focused on industrial design and supply chain.',
  name: 'Joseph Vijay',
  role: 'CEO, Bitlocker',
  image: '/images/home/vijay.png',
};

export const homeStats = [
  { id: 'devices', value: '1 Million+', label: 'Devices supported in reference deployments' },
  { id: 'brands', value: '50+', label: 'Brand programs on the platform' },
  { id: 'uptime', value: '99.9%', label: 'Target architecture for HA cloud tiers' },
];

/** Figma node 1001:2454 — closing banner */
export const homeClosingCta = {
  titleLines: ['Ready to Launch Your ', 'Smart Device ', 'Business?'] as const,
  subtitle:
    'Join hundreds of manufacturers who trust our platform. Get started in minutes with our comprehensive IoT solution.',
  cta: { label: 'Book Demo', to: '/contact-us' },
};
